<script lang="ts">
    import { twMerge } from 'tailwind-merge';
    import AvatarPlaceholder from './Placeholder.svelte';
    import Indicator from '../indicators/Indicator.svelte';

    export let src = '';
    export let href: string | undefined = undefined;
    export let rounded = false;
    export let border = false;
    export let stacked = false;
    export let selectable = false;
    export let selected = false;
    export let dot: object | undefined = undefined;
    export let alt = '';
    export let name = '';
    export let size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' = 'base';
    export let darkMode = false;

    $: dot = dot && { placement: 'top-right', color: 'gray', size: 'lg', ...dot };

    const sizes = {
        xs: 'w-4 h-4',
        sm: 'w-6 h-6',
        base: 'w-8 h-8',
        lg: 'w-12 h-12',
        xl: 'w-20 h-20',
        '2xl': 'w-36 h-36',
    };

    let wrapperClass: string;
    $: wrapperClass = twMerge(
        rounded ? 'rounded' : 'rounded-full',
        border && 'p-1 ring-4 ring-gray-300 dark:ring-gray-500',
        sizes[size],
        stacked && 'border-2 -ml-4 border-white dark:border-gray-800',
        'dark:bg-gray-600 text-gray-600 dark:text-gray-300',
        $$props.class,
    );
    let avatarClass: string;
    $: avatarClass = twMerge(
        rounded ? 'rounded' : 'rounded-full',
        selectable && 'hover:ring-4',
        selectable && darkMode ? 'hover:ring-gray-700' : 'hover:ring-gray-300',
        selected && 'ring-4',
        selectable && darkMode ? 'ring-gray-700' : 'ring-gray-300',
    );

    // 외부 클릭 감지를 위한 커스텀 액션
    function clickOutside(node, callback) {
        const handleClick = event => {
            if (node && !node.contains(event.target) && !event.defaultPrevented) {
                callback();
            }
        };

        document.addEventListener('click', handleClick, true);

        return {
            destroy() {
                document.removeEventListener('click', handleClick, true);
            },
        };
    }

    // 아바타 클릭 처리
    function handleAvatarClick(event) {
        if (selectable) {
            event.stopPropagation();
            selected = !selected;
        }
    }

    // 외부 클릭시 선택 해제
    function deselect() {
        if (selectable) {
            selected = false;
        }
    }
</script>

{#if !src || !!href || $$slots.default || dot}
    <svelte:element
        this={href ? 'a' : 'div'}
        use:clickOutside={deselect}
        on:click={handleAvatarClick}
        role={href ? 'link' : ''}
        tabindex="0"
        {href}
        {...$$restProps}
        class="relative flex items-center justify-center {wrapperClass}"
    >
        {#key selected}
            {#if src}
                <img {alt} {src} class={avatarClass} />
            {:else}
                <slot>
                    <AvatarPlaceholder {rounded} {darkMode} {name} class={avatarClass} />
                </slot>
            {/if}
            {#if dot}
                <Indicator border offset={rounded} {...dot} />
            {/if}
            {#if $$slots.badge}
                <div class="absolute bottom-0 right-0 h-[1.125rem] w-[1.125rem] -translate-y-[2px] translate-x-[3px]">
                    <slot name="badge" />
                </div>
            {/if}
        {/key}
    </svelte:element>
{:else}
    <img {alt} {src} {...$$restProps} class={twMerge(wrapperClass, avatarClass)} />
{/if}

<!--
  @component
  ## Features
  [Go to Avatar](https://flowbite-svelte.com/docs/components/avatar)
  - Bordered
  - Placeholder
  - Placeholder initials
  - Avatar tooltip
  - Dot indicator
  - Stacked
  - Avatar text
  - User dropdown
  - Sizes(xs-16px, sm-24px, base-32px, lg-48px, xl-80px, 2xl-144px)
  ## Props
  @prop src: string = '';
  @prop href: string | undefined = undefined;
  @prop rounded: boolean = false;
  @prop border: boolean = false;
  @prop stacked: boolean = false;
  @prop dot: object | undefined = undefined;
  @prop alt: string = '';
  @prop name: string = '';
  @prop size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' = 'base';
  ## Example
  ```
  <script>
    import { Avatar } from "@allibee/components/basic"
  </script>

  <div class="flex space-x-4">
    <Avatar src="/images/profile-picture-2.webp"/>
    <Avatar src="/images/profile-picture-2.webp" rounded/>
    <Avatar name="홍길동" />
    <Avatar name="Kim Min Su" rounded/>
  </div>
  ```
-->
